<template>
  <div class="ps-panel">
    <div class="ps-panel-hd">
      <h2>{{ $t('profile.appearance') }}</h2>
      <p>Personalize o tema e idioma da interface.</p>
    </div>
    <div class="ps-appear-row">
      <div class="ps-appear-info">
        <span class="ps-appear-label">Tema</span>
        <span class="ps-appear-desc">{{ isDarkMode ? 'Escuro ativado' : 'Claro ativado' }}</span>
      </div>
      <button class="btn btn-ghost dark-toggle" type="button" @click="toggleDarkMode">
        <span v-if="isDarkMode">☀ {{ $t('profile.lightMode') }}</span>
        <span v-else>☾ {{ $t('profile.darkMode') }}</span>
      </button>
    </div>
    <div class="ps-lang-block">
      <span class="ps-appear-label">{{ $t('profile.languageLabel') }}</span>
      <div class="lang-slider-wrap">
        <input type="range" class="lang-slider" :min="0" :max="supportedLocales.length - 1" :value="localeIndex" step="1" @input="onLocaleSlider($event)" />
        <div class="lang-slider-labels">
          <span v-for="(loc, i) in supportedLocales" :key="loc.code" class="lang-slider-tick" :class="{ active: localeIndex === i }" @click="switchLocale(loc.code)">{{ loc.flag }}</span>
        </div>
        <div class="lang-selected-label">{{ supportedLocales[localeIndex]?.flag }} {{ supportedLocales[localeIndex]?.label }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { themeStore } from '@/store/theme'
import { localeStore, SUPPORTED_LOCALES, type LocaleCode } from '@/store/locale'

export default defineComponent({
  name: 'ProfileAparenciaTab',
  setup() {
    const { t } = useI18n()
    const isDarkMode = computed(() => themeStore.isDark.value)
    const toggleDarkMode = () => themeStore.toggle()
    const supportedLocales = SUPPORTED_LOCALES
    const localeIndex = computed(() => {
      const idx = SUPPORTED_LOCALES.findIndex(l => l.code === localeStore.current.value)
      return idx >= 0 ? idx : 0
    })
    const switchLocale = (code: LocaleCode) => localeStore.set(code)
    const onLocaleSlider = (e: Event) => {
      const idx = Number((e.target as HTMLInputElement).value)
      if (SUPPORTED_LOCALES[idx]) switchLocale(SUPPORTED_LOCALES[idx].code)
    }
    return { isDarkMode, toggleDarkMode, supportedLocales, localeIndex, switchLocale, onLocaleSlider }
  }
})
</script>

<style scoped lang="scss">
.ps-panel-hd { margin-bottom: 24px; h2 { font-size: 18px; font-weight: 700; color: var(--ink); margin: 0 0 4px; } p { font-size: 13px; color: var(--ink-muted); margin: 0; } }

.ps-appear-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px; border: 1px solid var(--border); border-radius: 10px;
  background: var(--surface, #fff); margin-bottom: 20px;
}
.ps-appear-info { display: flex; flex-direction: column; gap: 2px; }
.ps-appear-label { font-size: 13px; font-weight: 600; color: var(--ink); }
.ps-appear-desc { font-size: 12px; color: var(--ink-muted); }

.ps-lang-block { display: flex; flex-direction: column; gap: 10px; }

.lang-slider-wrap { display: flex; flex-direction: column; gap: 8px; }
.lang-slider { width: 100%; accent-color: var(--accent, #0f4c81); cursor: pointer; height: 4px; }
.lang-slider-labels { display: flex; justify-content: space-between; padding: 0 2px; }
.lang-slider-tick {
  font-size: 18px; cursor: pointer; opacity: 0.45; transition: opacity .15s, transform .15s;
  user-select: none; line-height: 1;
  &.active { opacity: 1; transform: scale(1.25); }
  &:hover { opacity: 0.8; }
}
.lang-selected-label { font-size: 13px; font-weight: 600; color: var(--ink, #2c2b27); text-align: center; }

.dark-toggle { justify-content: flex-start; gap: 8px; font-size: 14px; }

.btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; border: 1px solid transparent; border-radius: var(--radius, 6px); padding: 6px 16px; font-size: 14px; font-weight: 600; cursor: pointer; transition: background .12s; }
.btn-ghost { background: transparent; color: var(--ink); border-color: var(--border-strong); &:hover { background: var(--surface-3); } }
</style>
